/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useMemo, useContext } from 'react';
import { I18nextProvider } from 'react-i18next';
import ConfigProvider, { ConfigContext } from 'contexts/config';
import App from 'App';
import { init as initI18n } from 'utils/i18n';
import { MemoryRouter, HashRouter } from 'react-router-dom';
import 'css/main.scss';

const I18nWrapper = ({ children }) => {
  const config = useContext(ConfigContext);
  const i18n = useMemo(
    () => initI18n({ explorerDir: config.options.explorerDir, locale: config.options.locale }),
    [config.options.explorerDir, config.options.locale]
  );

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
};

const RouterWrapper = ({ children }) => {
  const config = useContext(ConfigContext);
  let Router = MemoryRouter;
  let routerType = config.options.routerType ? config.options.routerType : config.constants.MEMORY_ROUTER;
  if (routerType === config.constants.HASH_ROUTER) {
    Router = HashRouter;
  } else if (routerType !== config.constants.MEMORY_ROUTER) {
    throw new Error(
      `Unsupported router type: '${config.options.routerType}'. Available options: ${config.constants.MEMORY_ROUTER}, ${config.constants.HASH_ROUTER}`
    );
  }
  return <Router>{children}</Router>;
};

const ApiExplorer = ({ options, apis, encodedApis, apps, docs }) => {
  return (
    <ConfigProvider options={options} apis={apis} encodedApis={encodedApis} apps={apps} docs={docs}>
      <I18nWrapper>
        <RouterWrapper>
          <App />
        </RouterWrapper>
      </I18nWrapper>
    </ConfigProvider>
  );
};

export default ApiExplorer;
